import { FileSpreadsheet, FileScan, GitMerge, BrainCircuit, ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * AgentFlow — n8n-style pipeline visualization.
 *
 * Shows the agentic reconciliation pipeline as a horizontal flow of nodes
 * connected by curved animated paths, like an n8n workflow. Decorative —
 * only used on the login page to communicate "this is an agent platform"
 * before the user has any data to look at.
 *
 * Five nodes, two AI agents (highlighted), three deterministic steps.
 * The animated stroke-dashoffset on connectors creates a flowing-data effect.
 */
interface NodeDef {
  id: string;
  title: string;
  subtitle: string;
  icon: ReactNode;
  /** AI agents glow; deterministic stages don't. */
  isAgent?: boolean;
  /** Tag below — e.g. model name, technique. */
  tag: string;
}

const NODES: NodeDef[] = [
  {
    id: 'portfolio',
    title: 'Portfolio Parser',
    subtitle: 'Holdings + transactions',
    icon: <FileSpreadsheet className="w-6 h-6" />,
    tag: 'ExcelJS',
  },
  {
    id: 'dp',
    title: 'DP Extractor',
    subtitle: 'Vision-grounded JSON',
    icon: <FileScan className="w-6 h-6" />,
    isAgent: true,
    tag: 'Claude Sonnet 4.5',
  },
  {
    id: 'match',
    title: 'Match Engine',
    subtitle: 'ISIN + qty + actions',
    icon: <GitMerge className="w-6 h-6" />,
    tag: 'Deterministic',
  },
  {
    id: 'classify',
    title: 'Root-Cause AI',
    subtitle: '17-code taxonomy',
    icon: <BrainCircuit className="w-6 h-6" />,
    isAgent: true,
    tag: 'Claude Opus 4.7',
  },
  {
    id: 'review',
    title: 'Maker · Checker',
    subtitle: 'Two-stage approval',
    icon: <ShieldCheck className="w-6 h-6" />,
    tag: 'RBAC + audit',
  },
];

export function AgentFlow() {
  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Pipeline. On md+ it's a horizontal row of nodes with
          horizontal connectors. On smaller screens (phone, narrow
          tablet portrait) it becomes a vertical stack with vertical
          connectors — five 80px nodes won't fit side-by-side on a
          phone, so we flow them top-to-bottom instead of hiding the
          diagram entirely. */}
      <div className="relative flex flex-col md:flex-row md:items-center md:justify-center gap-1 md:gap-0 py-3 md:py-4">
        {NODES.map((node, i) => (
          <div
            key={node.id}
            className="relative flex flex-col items-center md:flex-row md:items-center"
            style={{ flex: '0 0 auto' }}
          >
            <AgentNode node={node} index={i} />
            {i < NODES.length - 1 && <Connector />}
          </div>
        ))}
      </div>

      {/* Legend — wraps on narrow screens so the three items don't
          overflow horizontally on a phone. */}
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-1 text-xs text-ink-400">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-accent animate-pulse-dot" />
          AI agent
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-ink-400" />
          Deterministic stage
        </span>
        <span className="inline-flex items-center gap-2 font-mono tracking-wider uppercase">
          End-to-end ~ 90 seconds
        </span>
      </div>
    </div>
  );
}

function AgentNode({ node, index }: { node: NodeDef; index: number }) {
  return (
    <div
      className="relative flex flex-col items-center text-center"
      style={{ animation: `fadeUp 0.6s ${index * 0.15}s both ease-out` }}
    >
      <div
        className={`relative w-20 h-20 rounded-2xl flex items-center justify-center
          ${node.isAgent ? 'text-white' : 'text-ink-strong'}`}
        style={{
          background: node.isAgent
            ? 'linear-gradient(135deg, var(--accent-glow) 0%, var(--accent) 100%)'
            : 'var(--glass-bg-r)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: node.isAgent
            ? '1px solid var(--accent)'
            : '1px solid var(--glass-border)',
          boxShadow: node.isAgent
            ? 'var(--accent-shadow), inset 0 1px 0 0 rgba(255,255,255,0.20)'
            : 'var(--glass-shadow), inset 0 1px 0 0 var(--glass-edge)',
        }}
      >
        {node.icon}
        {node.isAgent && (
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-accent animate-pulse-dot" style={{ boxShadow: '0 0 12px var(--accent-glow)' }} />
        )}
      </div>
      <div className="mt-3 w-24">
        <div className="text-sm font-semibold text-ink-50 leading-tight tracking-tight">{node.title}</div>
        <div className="text-[10px] text-ink-400 mt-0.5 leading-tight">{node.subtitle}</div>
        <div className={`mt-1.5 inline-block px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider
          ${node.isAgent ? 'pill-accent' : 'pill-neutral'}`}
             style={{ borderRadius: '4px' }}>
          {node.tag}
        </div>
      </div>
    </div>
  );
}

/** Animated flowing connector between nodes. Horizontal on md+ (sits
 *  between nodes in a row); vertical on smaller screens (sits between
 *  stacked nodes). Two SVGs, toggled by breakpoint, so each can use the
 *  right viewBox orientation. */
function Connector() {
  return (
    <>
      {/* Vertical connector — mobile / narrow */}
      <svg
        className="md:hidden w-12 h-8 my-0.5"
        viewBox="0 0 48 120"
        preserveAspectRatio="none"
        fill="none"
        aria-hidden="true"
      >
        <path d="M 24 0 C 24 30, 24 90, 24 120" stroke="var(--glass-border-h)" strokeWidth="2" strokeLinecap="round" />
        <path d="M 24 0 C 24 30, 24 90, 24 120" stroke="url(#flowGradientV)" strokeWidth="2" strokeLinecap="round" strokeDasharray="6 14" className="animate-flow-dash" />
        <defs>
          <linearGradient id="flowGradientV" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FF1F6B" stopOpacity="0" />
            <stop offset="50%" stopColor="#FF4D8C" stopOpacity="1" />
            <stop offset="100%" stopColor="#4DA3FF" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      {/* Horizontal connector — md+. Fixed narrow width (not flex-1) so the
          connectors stay short and all five nodes fit on screen instead of
          the later ones overflowing off the right edge. */}
      <svg
        className="hidden md:block mx-0.5 h-12"
        style={{ width: '40px', flex: '0 0 40px' }}
        viewBox="0 0 40 48"
        preserveAspectRatio="none"
        fill="none"
        aria-hidden="true"
      >
        <path d="M 0 24 C 10 24, 30 24, 40 24" stroke="var(--glass-border-h)" strokeWidth="2" strokeLinecap="round" />
        <path d="M 0 24 C 10 24, 30 24, 40 24" stroke="url(#flowGradient)" strokeWidth="2" strokeLinecap="round" strokeDasharray="6 14" className="animate-flow-dash" />
        <defs>
          <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%"   stopColor="#FF1F6B" stopOpacity="0" />
            <stop offset="50%"  stopColor="#FF4D8C" stopOpacity="1" />
            <stop offset="100%" stopColor="#4DA3FF" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </>
  );
}
